import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import type { InterviewTypeListItemDto } from '@sara/api-client';
import { FILTER_REGISTRY } from './registry';
import { FilterChip } from './FilterChip';
import { AddFilterMenu } from './AddFilterMenu';
import type { FilterField } from './types';
import type { InterviewsSearch } from '../../../../router/tree';

interface FilterBarProps {
  search: InterviewsSearch;
  interviewTypes: InterviewTypeListItemDto[];
  onChange: (update: Partial<InterviewsSearch>) => void;
}

export function FilterBar({ search, interviewTypes, onChange }: FilterBarProps) {
  // Tracks which field's chip should auto-open its editor when it first mounts
  const [openOnAdd, setOpenOnAdd] = useState<FilterField | null>(null);
  // Fields explicitly added via the menu, kept until explicitly removed
  // (`handleRemove`) or closed without ever getting a value (`handlePendingClose`)
  // — never auto-pruned on activation. `isActive` below already makes an active
  // field visible on its own, so a field lingering here after going active is
  // harmless; pruning it eagerly instead unmounts its chip (and open editor) the
  // moment its value is cleared back out, which is worse.
  const [pendingFields, setPendingFields] = useState<FilterField[]>([]);

  const activeFilters = FILTER_REGISTRY.filter((def) => def.isActive(search));

  // Render chips for active filters + pending filters (registry order, de-duped)
  const visibleFilters = FILTER_REGISTRY.filter(
    (def) => def.isActive(search) || pendingFields.includes(def.field)
  );

  const hasFilters = activeFilters.length > 0;
  // All fields currently shown (active or pending) — used to hide them from AddFilterMenu
  const visibleFields = visibleFilters.map((d) => d.field);

  function handleAdd(field: FilterField) {
    setPendingFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
    setOpenOnAdd(field);
    // Apply any add-time defaults (e.g. completedNoResults defaults ON).
    const def = FILTER_REGISTRY.find((d) => d.field === field);
    if (def?.defaultParams) onChange({ ...def.defaultParams(), page: 1 });
  }

  function handleRemove(def: (typeof FILTER_REGISTRY)[number]) {
    // If it was pending (not yet active), just drop it from pending
    setPendingFields((prev) => prev.filter((f) => f !== def.field));
    setOpenOnAdd((prev) => (prev === def.field ? null : prev));
    onChange({ ...def.clearParams(search), page: 1 });
  }

  function handlePendingClose(field: FilterField) {
    // Editor closed without a value being chosen — remove the orphaned pending chip
    const def = FILTER_REGISTRY.find((d) => d.field === field);
    if (def && !def.isActive(search)) {
      setPendingFields((prev) => prev.filter((f) => f !== field));
    }
    setOpenOnAdd((prev) => (prev === field ? null : prev));
  }

  function handleClearAll() {
    const cleared = FILTER_REGISTRY.reduce(
      (acc, def) => ({ ...acc, ...def.clearParams(search) }),
      {} as Partial<InterviewsSearch>
    );
    setPendingFields([]);
    onChange({ ...cleared, page: 1 });
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visibleFilters.map((def) => (
        <FilterChip
          key={def.field}
          def={def}
          editorProps={{
            search,
            interviewTypes,
            onChange: (update) => onChange({ ...update, page: 1 }),
            onClose: () => handlePendingClose(def.field),
          }}
          onRemove={() => handleRemove(def)}
          defaultOpen={openOnAdd === def.field}
        />
      ))}

      {visibleFields.length < FILTER_REGISTRY.length && (
        <AddFilterMenu activeFields={visibleFields} onAdd={handleAdd} />
      )}

      {hasFilters && (
        <Button
          variant="ghost"
          shape="pill"
          size="sm"
          className="h-7 gap-1 px-2.5 text-[12.5px] text-muted-foreground hover:text-foreground"
          onClick={handleClearAll}
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.6} />
          Clear all
        </Button>
      )}
    </div>
  );
}
